// Single source of truth for per-route SEO + Open Graph metadata. Consumed two ways:
//   1. At runtime by each route's `head: () => seoHead(path)` (TanStack Router renders
//      the tags via <HeadContent />; React 19 hoists <title>/<meta>/<link> into <head>).
//   2. At build time by scripts/prerender.ts, which snapshots the rendered <head> into
//      each route's static HTML so JS-free crawlers (iMessage, Slack, X, LinkedIn) and
//      search engines see correct per-page tags.
// House style (marketing content-authoring): no em-dashes in titles/descriptions.

export const SITE = {
  name: 'Mindrealm',
  origin: 'https://mindrealm.ai',
  defaultOgImage: '/og/default.png',
  twitterCard: 'summary_large_image',
  twitterSite: '@mindrealm_ai',
  twitterCreator: '@mindrealm_ai',
  locale: 'en_US',
  ogImageWidth: '1200',
  ogImageHeight: '630',
} as const;

export interface RouteSeo {
  title: string;
  description: string;
  /** Per-route OG image override (absolute path under /). Defaults to SITE.defaultOgImage. */
  ogImage?: string;
  /** App/auth pages: keep out of search results. */
  noindex?: boolean;
}

export type MetaTag =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string };

export interface LinkTag {
  rel: string;
  href: string;
}

// Every route in the router has an entry. The drift-guard test asserts key parity, so
// adding a route without metadata fails loudly.
export const ROUTE_META: Record<string, RouteSeo> = {
  '/': {
    title: 'Mindrealm: deterministic code review for AI-generated code',
    description:
      'AI writes code fast but fails confidently. Mindrealm runs 75+ deterministic checks across Go, Python, TypeScript, and Rust to catch the bugs your agents miss.',
  },
  '/philosophy': {
    title: 'Philosophy | Mindrealm',
    description:
      "Why judgment enhances generation: confidence isn't correctness, the bottleneck moved to verification, and deterministic review beats vibes.",
  },
  '/pricing': {
    title: 'Pricing | Mindrealm',
    description:
      'One price, every check included. $30 per seat per month, 7-day free trial. No usage fees, no tiers. GitHub App reviews, CLI, and the stop hook auto-fix loop.',
  },
  '/consulting': {
    title: 'Consulting | Mindrealm',
    description:
      "AI made your team faster. I'll help make them better. Audits, guardrails, and coaching to make AI-generated code safer to ship. Fixed-price audit from $5,000.",
  },
  '/getting-started': {
    title: 'Getting started | Mindrealm',
    description:
      'Get reviewing in minutes: GitHub App PR reviews, the terminal CLI, or the stop hook auto-fix loop for Claude Code, Codex, Kimi Code, and Antigravity CLI.',
  },
  '/faq': {
    title: 'FAQ | Mindrealm',
    description:
      'Answers on supported languages, agent compatibility, what gets checked, code privacy, self-hosting, and how Mindrealm differs from linters and LLM reviewers.',
  },
  '/docs': {
    title: 'Documentation | Mindrealm',
    description:
      'The full reference for the mind CLI: install, commands, 75+ checks across 9 categories, scope, suppressions, gates, hooks, configuration, and custom rules.',
  },
  '/contact': {
    title: 'Contact | Mindrealm',
    description:
      'Questions, feedback, or a deployment that needs something special? Get in touch with the Mindrealm team.',
  },
  '/terms': {
    title: 'Terms of Service | Mindrealm',
    description: 'The terms governing your use of Mindrealm.',
  },
  '/privacy': {
    title: 'Privacy Policy | Mindrealm',
    description:
      'How Mindrealm handles your data and source code: cloned for review only, never trained on, never sold.',
  },
  '/sign-in': {
    title: 'Sign in | Mindrealm',
    description: 'Sign in to your Mindrealm account.',
    noindex: true,
  },
  '/dashboard': {
    title: 'Dashboard | Mindrealm',
    description: 'Manage your Mindrealm organization, team, and billing.',
    noindex: true,
  },
  '/admin': {
    title: 'Admin | Mindrealm',
    description: 'Internal Mindrealm workflow redirect.',
    noindex: true,
  },
  '/review': {
    title: 'Review | Mindrealm',
    description: 'Review evidence and certify Mindrealm work.',
    noindex: true,
  },
  '/tasks': {
    title: 'Tasks | Mindrealm',
    description: 'Operate and inspect Mindrealm Tasks and Attempts.',
    noindex: true,
  },
  '/telemetry': {
    title: 'Telemetry | Mindrealm',
    description: 'Open Mindrealm operational telemetry.',
    noindex: true,
  },
  '/onboarding': {
    title: 'Get started | Mindrealm',
    description: 'Set up your Mindrealm account and start your free trial.',
    noindex: true,
  },
  '/accept-invitation': {
    title: 'Accept invitation | Mindrealm',
    description: 'Join your team on Mindrealm.',
    noindex: true,
  },
};

/** All routes that should be statically prerendered and indexed (drives the sitemap). */
export const PRERENDER_ROUTES: string[] = Object.entries(ROUTE_META)
  .filter(([, meta]) => !meta.noindex)
  .map(([path]) => path);

const absolute = (path: string): string =>
  path.startsWith('http') ? path : `${SITE.origin}${path}`;

/**
 * Build the TanStack Router `head()` payload for a route path. Returns the full meta +
 * canonical link set; the same data feeds the build-time prerender.
 */
export function seoHead(path: string): { meta: MetaTag[]; links: LinkTag[] } {
  const route = ROUTE_META[path];
  if (!route) {
    throw new Error(`No ROUTE_META entry for "${path}"`);
  }

  const url = absolute(path);
  const ogImage = absolute(route.ogImage ?? SITE.defaultOgImage);

  const meta: MetaTag[] = [
    { title: route.title },
    { name: 'description', content: route.description },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: SITE.name },
    { property: 'og:title', content: route.title },
    { property: 'og:description', content: route.description },
    { property: 'og:url', content: url },
    { property: 'og:locale', content: SITE.locale },
    { property: 'og:image', content: ogImage },
    { property: 'og:image:width', content: SITE.ogImageWidth },
    { property: 'og:image:height', content: SITE.ogImageHeight },
    { property: 'og:image:alt', content: route.title },
    { name: 'twitter:card', content: SITE.twitterCard },
    { name: 'twitter:site', content: SITE.twitterSite },
    { name: 'twitter:creator', content: SITE.twitterCreator },
    { name: 'twitter:title', content: route.title },
    { name: 'twitter:description', content: route.description },
    { name: 'twitter:image', content: ogImage },
  ];

  if (route.noindex) {
    meta.push({ name: 'robots', content: 'noindex,nofollow' });
  }

  return { meta, links: [{ rel: 'canonical', href: url }] };
}
